import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  asLink?: string;
  href?: string;
  target?: string;
  rel?: string;
}

/**
 * Refined button component with Apple-like design.
 * Supports three variants (primary, secondary, ghost) and three sizes (sm, md, lg).
 * Can be rendered as a button, React Router link, or external link.
 *
 * @example
 * // As button
 * <Button variant="primary" size="md">Click me</Button>
 *
 * // As React Router link
 * <Button variant="secondary" asLink="/about">Learn More</Button>
 *
 * // As external link
 * <Button variant="ghost" href="https://example.com" target="_blank">Visit</Button>
 *
 * // Loading state
 * <Button isLoading disabled>Processing...</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      asLink,
      href,
      children,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const buttonClassName = `button button--${variant} button--${size} ${className}`;

    const content = (
      <>
        {isLoading && <span className="button__spinner" />}
        <span className="button__text">{children}</span>
      </>
    );

    // React Router Link
    if (asLink) {
      return (
        <Link
          to={asLink}
          className={buttonClassName}
          aria-disabled={disabled || isLoading}
        >
          {content}
        </Link>
      );
    }

    // External link
    if (href) {
      return (
        <a
          href={href}
          className={buttonClassName}
          aria-disabled={disabled || isLoading}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {content}
        </a>
      );
    }

    // Standard button
    return (
      <button
        ref={ref}
        className={buttonClassName}
        disabled={disabled || isLoading}
        {...props}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';
